/**
 * The Infrastruktur page's pure helpers: formatting, the level → colour mapping and the catalogue
 * keys built at runtime. JSX-free on purpose, like the rest of `src/lib`, so the dashboard's Jest
 * (node, no jsdom) can test them.
 *
 * Every level, percentage and sum on the page is decided by the API
 * (`apps/api/src/app/internal-admin/infrastructure/infrastructure-snapshot.ts`); this file only
 * draws them. A `null` figure renders as an em dash — never as `0`, so a silent node never reads
 * as an idle one.
 */
import {
  OpsInfrastructureLevel,
  OpsInfrastructureSourceState,
  OpsInfrastructureState,
  OpsNodeStatus,
} from '../../../../libs/api-client/src/generated/models';
import { intlLocale } from '../i18n/formats';
import type { SupportedLocale } from '../i18n';

/** Where the page lives. */
export const INFRASTRUCTURE_PATH = '/infrastructure';

/** The collector writes every 15 s; polling faster only re-reads the same snapshot. */
export const INFRASTRUCTURE_POLL_MS = 15_000;

/** What a missing figure renders as. */
export const MISSING = '—';

/**
 * One level's colours for the marks the page draws itself (ring gauges, meters): the fill and its
 * track — a light step of the same hue, so state reads across the whole mark. The values are the
 * Tailwind steps the rest of the dashboard already uses (`chartTheme.ts`, `Pill.tsx`): teal is the
 * brand and means "fine", amber and red are the ordinary warning signals. Status colours always
 * travel with their word ({@link levelTone} → `Badge`), never colour alone.
 */
export interface LevelColour {
  readonly mark: string;
  readonly track: string;
}

/** Level → colour: `primary-600`, `amber-500`, `red-500`, `gray-300`, each on its `-100` track. */
export const LEVEL_COLOURS: Record<OpsInfrastructureLevel, LevelColour> = {
  [OpsInfrastructureLevel.Ok]: { mark: '#0d9488', track: '#ccfbf1' },
  [OpsInfrastructureLevel.Warn]: { mark: '#f59e0b', track: '#fef3c7' },
  [OpsInfrastructureLevel.Critical]: { mark: '#ef4444', track: '#fee2e2' },
  [OpsInfrastructureLevel.Unknown]: { mark: '#d1d5db', track: '#f3f4f6' },
};

/** The dashboard-wide tone (`Pill`/`Badge`/`TONE_TEXT`) of a level. */
export function levelTone(
  level: OpsInfrastructureLevel,
): 'teal' | 'amber' | 'red' | 'gray' {
  switch (level) {
    case OpsInfrastructureLevel.Ok:
      return 'teal';
    case OpsInfrastructureLevel.Warn:
      return 'amber';
    case OpsInfrastructureLevel.Critical:
      return 'red';
    default:
      return 'gray';
  }
}

/** The tone of a node's reporting status. */
export function nodeStatusTone(
  status: OpsNodeStatus,
): 'green' | 'red' | 'gray' {
  switch (status) {
    case OpsNodeStatus.Live:
      return 'green';
    case OpsNodeStatus.Stale:
      return 'red';
    default:
      return 'gray';
  }
}

/** The tone of a data source's state. */
export function sourceStateTone(
  state: OpsInfrastructureSourceState,
): 'green' | 'amber' | 'red' | 'gray' {
  switch (state) {
    case OpsInfrastructureSourceState.Ok:
      return 'green';
    case OpsInfrastructureSourceState.NotConfigured:
      return 'gray';
    case OpsInfrastructureSourceState.RateLimited:
      return 'amber';
    default:
      return 'red';
  }
}

/** The catalogue key of a level's label. */
export function levelLabelKey(level: OpsInfrastructureLevel): string {
  return `infrastructure.level.${level}`;
}

/** The catalogue key of a node status. */
export function nodeStatusLabelKey(status: OpsNodeStatus): string {
  return `infrastructure.nodeStatus.${status}`;
}

/** The catalogue key of a data source's state. */
export function sourceStateLabelKey(
  state: OpsInfrastructureSourceState,
): string {
  return `infrastructure.sourceState.${state}`;
}

/** The catalogue key of the endpoint's overall state. */
export function pageStateLabelKey(state: OpsInfrastructureState): string {
  return `infrastructure.pageState.${state}`;
}

/** Every catalogue key built at runtime from an enum — for the catalogue spec. */
export const RUNTIME_LABEL_KEYS: readonly string[] = [
  ...Object.values(OpsInfrastructureLevel).map(levelLabelKey),
  ...Object.values(OpsNodeStatus).map(nodeStatusLabelKey),
  ...Object.values(OpsInfrastructureSourceState).map(sourceStateLabelKey),
  ...Object.values(OpsInfrastructureState).map(pageStateLabelKey),
];

/** A number in the reader's locale, with at most `digits` decimals. */
export function formatNumber(
  value: number | null | undefined,
  locale: SupportedLocale,
  digits = 0,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return MISSING;
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  }).format(value);
}

/** A percentage, `42 %` / `42%` as the locale sets it. */
export function formatPercent(
  value: number | null | undefined,
  locale: SupportedLocale,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return MISSING;
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    maximumFractionDigits: value < 10 ? 1 : 0,
  }).format(value / 100);
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** Bytes in binary steps with decimal-looking units, the way `free -h` and `df -h` print them. */
export function formatBytes(
  value: number | null | undefined,
  locale: SupportedLocale,
): string {
  if (
    value === null ||
    value === undefined ||
    !Number.isFinite(value) ||
    value < 0
  ) {
    return MISSING;
  }
  let scaled = value;
  let unit = 0;
  while (scaled >= 1024 && unit < BYTE_UNITS.length - 1) {
    scaled /= 1024;
    unit++;
  }
  return `${formatNumber(scaled, locale, scaled < 10 && unit > 0 ? 1 : 0)} ${BYTE_UNITS[unit]}`;
}

/** Bytes per second as bits per second — how network throughput is read. */
export function formatBitRate(
  bytesPerSecond: number | null | undefined,
  locale: SupportedLocale,
): string {
  if (
    bytesPerSecond === null ||
    bytesPerSecond === undefined ||
    !Number.isFinite(bytesPerSecond) ||
    bytesPerSecond < 0
  ) {
    return MISSING;
  }
  const units = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s'] as const;
  let scaled = bytesPerSecond * 8;
  let unit = 0;
  while (scaled >= 1000 && unit < units.length - 1) {
    scaled /= 1000;
    unit++;
  }
  return `${formatNumber(scaled, locale, scaled < 10 ? 1 : 0)} ${units[unit]}`;
}

/** A duration as its two largest units: `3 d 4 h`, `12 min 5 s`, `42 s`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (
    seconds === null ||
    seconds === undefined ||
    !Number.isFinite(seconds) ||
    seconds < 0
  ) {
    return MISSING;
  }
  const whole = Math.floor(seconds);
  const parts: [number, string][] = [
    [Math.floor(whole / 86_400), 'd'],
    [Math.floor((whole % 86_400) / 3600), 'h'],
    [Math.floor((whole % 3600) / 60), 'min'],
    [whole % 60, 's'],
  ];
  const first = parts.findIndex(([value]) => value > 0);
  if (first === -1) return '0 s';
  return parts
    .slice(first, first + 2)
    .filter(([value]) => value > 0)
    .map(([value, unit]) => `${value} ${unit}`)
    .join(' ');
}

/** A unix-seconds time as `HH:mm` in the reader's locale. */
export function formatClock(
  unixSeconds: number,
  locale: SupportedLocale,
): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(unixSeconds * 1000));
}

/** The ring gauge's stroke geometry for a percentage (clamped to 0–100, null = empty). */
export function ringDash(
  percent: number | null | undefined,
  radius: number,
): { circumference: number; offset: number } {
  const circumference = 2 * Math.PI * radius;
  const clamped =
    percent === null || percent === undefined || !Number.isFinite(percent)
      ? 0
      : Math.min(100, Math.max(0, percent));
  return { circumference, offset: circumference * (1 - clamped / 100) };
}

/** A meter's fill width, clamped to 0–100 % (a ceiling above 100 % still draws a full bar). */
export function meterWidth(percent: number | null | undefined): string {
  if (percent === null || percent === undefined || !Number.isFinite(percent)) {
    return '0%';
  }
  return `${Math.min(100, Math.max(0, percent))}%`;
}

/** How many nodes report live, out of all known. */
export function liveCount(nodes: readonly { status: OpsNodeStatus }[]): {
  live: number;
  total: number;
} {
  return {
    live: nodes.filter((node) => node.status === OpsNodeStatus.Live).length,
    total: nodes.length,
  };
}

/** The worst of several levels — what a node card's frame shows. */
export function worstLevel(
  levels: readonly OpsInfrastructureLevel[],
): OpsInfrastructureLevel {
  if (levels.includes(OpsInfrastructureLevel.Critical)) {
    return OpsInfrastructureLevel.Critical;
  }
  if (levels.includes(OpsInfrastructureLevel.Warn)) {
    return OpsInfrastructureLevel.Warn;
  }
  if (levels.includes(OpsInfrastructureLevel.Ok))
    return OpsInfrastructureLevel.Ok;
  return OpsInfrastructureLevel.Unknown;
}

/**
 * The prefix every node name shares (`easy-invoices-production-`), cut at its last `-`, so a dense
 * table can show `api-1` where the full name would truncate. Empty with fewer than two names or no
 * shared prefix — then names are shown whole.
 */
export function commonNodePrefix(names: readonly string[]): string {
  if (names.length < 2) return '';
  let prefix = names[0];
  for (const name of names.slice(1)) {
    while (!name.startsWith(prefix)) prefix = prefix.slice(0, -1);
  }
  const cut = prefix.lastIndexOf('-');
  return cut > 0 ? prefix.slice(0, cut + 1) : '';
}

/** A label with the shared node prefix removed (labels start with a node name, or are an IP). */
export function shortLabel(label: string, prefix: string): string {
  return prefix && label.startsWith(prefix) && label.length > prefix.length
    ? label.slice(prefix.length)
    : label;
}
